import { config } from './config';

export interface AuthUser {
  name: string;
  email: string;
  idToken: string;
  accessToken: string;
  roles?: string[];
}

interface TokenResponse {
  id_token: string;
  access_token: string;
  refresh_token?: string;
}

const fetchTokens = async (code: string): Promise<TokenResponse> => {
  try {
    const params = new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: config.clientId,
      code,
      redirect_uri: window.location.origin,
    });

    console.log('Fetching tokens with params:', params.toString());

    const response = await fetch(`${config.cognitoTokenUrl}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: params.toString(),
    });

    if (!response.ok) {
      const errorData = await response.json();
      console.error('Error fetching tokens:', errorData);
      throw new Error(`Error fetching tokens: ${errorData.error}`);
    }

    const data = await response.json();

    return {
      id_token: data.id_token,
      access_token: data.access_token,
      refresh_token: data.refresh_token, // may be undefined
    };
  } catch (error) {
    console.error('Error fetching tokens:', error);
    throw error;
  }
};

const fetchUserInfo = async (access_token: string): Promise<AuthUser> => {
  const response = await fetch(`${config.cognitoUserInfoUrl}`, {
    headers: {
      Authorization: `Bearer ${access_token}`,
    },
  });

  if (!response.ok) {
    const errorData = await response.json();
    console.error('Error fetching user info:', errorData);
    throw new Error(`Error fetching user info: ${errorData.error}`);
  }

  const userInfo = await response.json();
  return {
    name: userInfo.name,
    email: userInfo.email,
    idToken: access_token, // or id_token if needed
    accessToken: access_token,
  };
};

export const checkUser = async (): Promise<AuthUser | null> => {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');

    if (code) {
      const { id_token, access_token, refresh_token } = await fetchTokens(code);

      localStorage.setItem('id_token', id_token);
      localStorage.setItem('access_token', access_token);
      if (refresh_token) {
        localStorage.setItem('refresh_token', refresh_token);
      }

      // Clean up URL after exchanging code
      window.history.replaceState({}, document.title, window.location.pathname);

      const userInfo = await fetchUserInfo(access_token);
      if (userInfo && userInfo.email) {
        return userInfo;
      } else {
        console.error('Invalid user info:', userInfo);
        return null;
      }
    } else {
      const id_token = localStorage.getItem('id_token');
      const access_token = localStorage.getItem('access_token');

      if (!id_token || !access_token || id_token === 'undefined') {
        window.location.href = `${config.cognitoLoginUrl}?client_id=${config.clientId}&response_type=code&scope=email+openid+phone+profile&redirect_uri=${encodeURIComponent(window.location.origin)}`;
        return null;
      }

      const userInfo = await fetchUserInfo(access_token);
      if (userInfo && userInfo.email) {
        return userInfo;
      } else {
        console.error('Invalid user info:', userInfo);
        return null;
      }
    }
  } catch (error) {
    console.error('Error checking user:', error);
    return null;
  }
};
